import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Project, NewProjectForm, ProjectSortField, ProjectSortOrder, ProjectSource } from '../types';
import { BackendAPI } from '../lib/api';

// --- ProjectStore Interface ---
// Defines the shape of our project management state and actions.

interface ProjectStoreState {
  projects: Project[];
  filteredProjects: Project[];
  selectedProject: Project | null;
  /**
   * Persisted id of the active project. Survives reloads and tab switches
   * (the full `selectedProject` object is re-hydrated from the backend by
   * ProjectContextHeader). This is the only field written to localStorage.
   */
  selectedProjectId: string | null;
  loading: boolean;
  /**
   * Error from a project-LIST operation (fetchProjects). Read by the
   * Dashboard. Kept separate from `selectedProjectError` so that a failed
   * single-project fetch can never blank the dashboard's project list.
   */
  error: string | null;
  /**
   * Error from a single-project operation (getProjectById). Read by
   * ProjectDetailPage. The always-mounted ProjectContextHeader also calls
   * getProjectById to rehydrate the sticky project; routing its failures
   * here (not into `error`) prevents a stale/foreign selected project from
   * surfacing "Access denied" on the dashboard. Bug: dashboard-shared-error.
   */
  selectedProjectError: string | null;
  searchQuery: string;
  sortField: ProjectSortField;
  sortOrder: ProjectSortOrder;
  sourceFilter: ProjectSource | 'all';
}

interface ProjectStoreActions {
  fetchProjects: () => Promise<void>;
  getProjectById: (id: string) => Promise<Project | undefined>;
  setSelectedProject: (project: Project | null) => void;
  createProject: (newProjectData: NewProjectForm) => Promise<Project | null>;
  updateProject: (id: string, updatedData: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  archiveProject: (id: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setSortField: (field: ProjectSortField) => void;
  setSortOrder: (order: ProjectSortOrder) => void;
  setSourceFilter: (source: ProjectSource | 'all') => void;
  /** B-509: wipe ALL user-scoped state (projects, the selected project + its
   * id, errors, filters). Called on logout / user-switch so one account's data
   * never bleeds into the next on a shared browser. Also clears the persisted
   * `aiea-project-store` selectedProjectId. */
  reset: () => void;
}

type ProjectStore = ProjectStoreState & ProjectStoreActions;

// --- Helper Function for Filtering, Sorting ---

/**
 * Filters projects by search query and source, then sorts.
 *
 * Args:
 *   projects (Project[]): The full list of projects.
 *   query (string): Search query string.
 *   sourceFilter (ProjectSource | 'all'): Source filter value.
 *   sortField (ProjectSortField): Field to sort by.
 *   sortOrder (ProjectSortOrder): Sort direction.
 *
 * Returns:
 *   Project[]: Filtered and sorted project list.
 */
const filterAndSortProjects = (
  projects: Project[],
  query: string,
  sourceFilter: ProjectSource | 'all',
  sortField: ProjectSortField,
  sortOrder: ProjectSortOrder
): Project[] => {
  let result = [...projects];

  // Reason: Filter out archived projects from the main view
  result = result.filter((p) => p.status !== 'archived');

  // Apply source filter. The backend surfaces `source` top-level (it strips
  // metadata), so read p.source; fall back to legacy metadata.source.
  if (sourceFilter !== 'all') {
    result = result.filter(
      (p) => (p.source ?? (p.metadata?.source as string)) === sourceFilter
    );
  }

  // Apply search query
  if (query) {
    const lowercasedQuery = query.toLowerCase();
    result = result.filter(
      (project) =>
        (project.owner_name || '').toLowerCase().includes(lowercasedQuery) ||
        (project.address || '').toLowerCase().includes(lowercasedQuery) ||
        (project.claim_number || '').toLowerCase().includes(lowercasedQuery)
    );
  }

  // Apply sorting
  result.sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'date': {
        const dateA = new Date(a.date_of_loss || a.created_at || '').getTime() || 0;
        const dateB = new Date(b.date_of_loss || b.created_at || '').getTime() || 0;
        comparison = dateA - dateB;
        break;
      }
      case 'name': {
        const nameA = (a.address || a.owner_name || '').toLowerCase();
        const nameB = (b.address || b.owner_name || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
        break;
      }
      case 'status': {
        const statusA = (a.status || 'active').toLowerCase();
        const statusB = (b.status || 'active').toLowerCase();
        comparison = statusA.localeCompare(statusB);
        break;
      }
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  return result;
};

// --- Zustand Store Implementation ---

const useProjectStore = create<ProjectStore>()(
  persist(
    (set, get) => ({
  // --- STATE ---
  projects: [],
  filteredProjects: [],
  selectedProject: null,
  selectedProjectId: null,
  loading: false,
  error: null,
  selectedProjectError: null,
  searchQuery: '',
  sortField: 'date',
  sortOrder: 'desc',
  sourceFilter: 'all',

  // --- ACTIONS ---

  /**
   * B-509: clear every user-scoped field so no prior account's data lingers
   * after logout / a user switch. Resets to the pristine initial state; the
   * persist middleware then writes the cleared selectedProjectId to storage.
   */
  reset: () => {
    set({
      projects: [],
      filteredProjects: [],
      selectedProject: null,
      selectedProjectId: null,
      loading: false,
      error: null,
      selectedProjectError: null,
      searchQuery: '',
      sourceFilter: 'all',
    });
  },

  /**
   * Fetches all projects for the authenticated user from the backend.
   */
  fetchProjects: async () => {
    set({ loading: true, error: null });
    try {
      const projects = await BackendAPI.getProjects();
      const { searchQuery, sourceFilter, sortField, sortOrder } = get();
      set({
        projects,
        filteredProjects: filterAndSortProjects(projects, searchQuery, sourceFilter, sortField, sortOrder),
        loading: false,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch projects';
      set({ error: errorMessage, loading: false });
    }
  },

  /**
   * Fetches a single project by its ID.
   * Caches the result in the main projects list.
   */
  getProjectById: async (id: string) => {
    // First, check if the project is already in the store
    const existingProject = get().projects.find((p) => p.id === id);
    if (existingProject) {
      set({
        selectedProject: existingProject,
        selectedProjectId: existingProject.id,
        selectedProjectError: null,
      });
      return existingProject;
    }

    // Reason: only touch `selectedProjectError`, never the list-level `error`.
    // The Dashboard reads `error`; if this single-project fetch failed (e.g.
    // the sticky/persisted project belongs to another account → 403) it must
    // not blank the dashboard's successfully-loaded project list.
    set({ loading: true, selectedProjectError: null });
    try {
      const project = await BackendAPI.getProject(id);
      if (project) {
        // Add to the list if it's not there already
        set((state) => ({
          projects: [...state.projects.filter((p) => p.id !== id), project],
          selectedProject: project,
          selectedProjectId: project.id,
          selectedProjectError: null,
        }));
      }
      return project;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch project';
      // Reason: self-heal a stale/inaccessible persisted selection. If the id
      // we failed on is the remembered active project, clear it so the header
      // stops re-fetching it on every page load (otherwise the bad id sticks).
      set((state) => ({
        selectedProjectError: errorMessage,
        ...(state.selectedProjectId === id
          ? { selectedProjectId: null, selectedProject: null }
          : {}),
      }));
      return undefined;
    } finally {
      set({ loading: false });
    }
  },

  /**
   * Sets the currently selected project in the state.
   */
  setSelectedProject: (project) => {
    set({ selectedProject: project, selectedProjectId: project?.id ?? null });
  },

  /**
   * Creates a new project.
   */
  createProject: async (newProjectData) => {
    set({ loading: true, error: null });
    try {
      const newProject = await BackendAPI.createProject(newProjectData);
      if (newProject) {
        set((state) => {
          const updatedProjects = [...state.projects, newProject];
          return {
            projects: updatedProjects,
            filteredProjects: filterAndSortProjects(
              updatedProjects,
              state.searchQuery,
              state.sourceFilter,
              state.sortField,
              state.sortOrder
            ),
          };
        });
      }
      return newProject;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create project';
      set({ error: errorMessage });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  /**
   * Updates an existing project.
   */
  updateProject: async (id, updatedData) => {
    const { projects, searchQuery, sourceFilter, sortField, sortOrder } = get();
    const originalProjects = projects;
    // Optimistic update
    const updatedProjects = originalProjects.map((p) =>
      p.id === id ? { ...p, ...updatedData } : p
    );
    set({
      projects: updatedProjects,
      filteredProjects: filterAndSortProjects(updatedProjects, searchQuery, sourceFilter, sortField, sortOrder),
    });

    try {
      await BackendAPI.updateProject(id, updatedData);
    } catch {
      // Revert on failure
      set({
        projects: originalProjects,
        filteredProjects: filterAndSortProjects(originalProjects, searchQuery, sourceFilter, sortField, sortOrder),
        error: 'Failed to update project',
      });
    }
  },

  /**
   * Deletes a project.
   */
  deleteProject: async (id) => {
    const { projects, searchQuery, sourceFilter, sortField, sortOrder } = get();
    const originalProjects = projects;
    // Optimistic update
    const updatedProjects = originalProjects.filter((p) => p.id !== id);
    set({
      projects: updatedProjects,
      filteredProjects: filterAndSortProjects(updatedProjects, searchQuery, sourceFilter, sortField, sortOrder),
    });

    try {
      await BackendAPI.deleteProject(id);
    } catch {
      // Revert on failure
      set({
        projects: originalProjects,
        filteredProjects: filterAndSortProjects(originalProjects, searchQuery, sourceFilter, sortField, sortOrder),
        error: 'Failed to delete project',
      });
    }
  },

  /**
   * Archives a project by setting status to 'archived'.
   * Removes from the visible list optimistically.
   */
  archiveProject: async (id) => {
    const { projects, searchQuery, sourceFilter, sortField, sortOrder } = get();
    const originalProjects = projects;
    // Optimistic update: mark as archived
    const updatedProjects = originalProjects.map((p) =>
      p.id === id ? { ...p, status: 'archived' } : p
    );
    set({
      projects: updatedProjects,
      filteredProjects: filterAndSortProjects(updatedProjects, searchQuery, sourceFilter, sortField, sortOrder),
    });

    try {
      await BackendAPI.archiveProject(id);
    } catch {
      // Revert on failure
      set({
        projects: originalProjects,
        filteredProjects: filterAndSortProjects(originalProjects, searchQuery, sourceFilter, sortField, sortOrder),
        error: 'Failed to archive project',
      });
    }
  },

  /**
   * Sets the search query and updates the filtered projects list.
   */
  setSearchQuery: (query) => {
    set((state) => ({
      searchQuery: query,
      filteredProjects: filterAndSortProjects(state.projects, query, state.sourceFilter, state.sortField, state.sortOrder),
    }));
  },

  /**
   * Sets the sort field and re-filters/sorts.
   */
  setSortField: (field) => {
    set((state) => ({
      sortField: field,
      filteredProjects: filterAndSortProjects(state.projects, state.searchQuery, state.sourceFilter, field, state.sortOrder),
    }));
  },

  /**
   * Sets the sort order and re-filters/sorts.
   */
  setSortOrder: (order) => {
    set((state) => ({
      sortOrder: order,
      filteredProjects: filterAndSortProjects(state.projects, state.searchQuery, state.sourceFilter, state.sortField, order),
    }));
  },

  /**
   * Sets the source filter and re-filters/sorts.
   */
  setSourceFilter: (source) => {
    set((state) => ({
      sourceFilter: source,
      filteredProjects: filterAndSortProjects(state.projects, state.searchQuery, source, state.sortField, state.sortOrder),
    }));
  },
    }),
    {
      name: 'aiea-project-store',
      // Reason: only the active project id is durable. Project lists and the
      // hydrated selectedProject object are re-fetched from the backend, so
      // persisting them would risk serving stale data after a reload.
      partialize: (state) => ({ selectedProjectId: state.selectedProjectId }),
    },
  ),
);

export default useProjectStore;
